import { nwccDomain } from "@js/nwcc-domains.js";
import { renderGrowl } from "@js/nwcc-growl.js";
import { getQueryArgs, getBasinStates } from "@js/nwcc-navbar.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { getStateBasins } from "../model/get-data.js";
import { replaceHashWithSearchArg } from "@js/nwcc-utils.js";

const plotBaseUrl = `${nwccDomain}/awdb/basin-plots`;
export const monthNames = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const currDate = new Date();
export const currMonth = monthNames[currDate.getMonth()];

const validStatesMap = getBasinStates().reduce((objEntry, stateObj) => {
    objEntry[stateObj.abbr] = stateObj.name;
    return objEntry;
}, {});
const validStates = Object.keys(validStatesMap);

function getPageArgs() {
    return [
        {
            argName: "state",
            argDesc: "2-digit state code to filter plot dropdowns by.",
            argExample: "UT",
            argOptions: validStates,
        },
    ];
}

window.onload = windowOnLoad;

export async function windowOnLoad() {
    setArgDetailsData(getPageArgs());
    installOpenPlotListeners();
    const args = getQueryArgs();
    Object.keys(args).forEach((key) => {
        if (validStates.includes(args[key].toUpperCase()) && !Object.keys(args).includes("state")) {
            args["state"] = args[key].toUpperCase();
            replaceHashWithSearchArg(`?state=${args["state"]}`);
        }
    });
    const state = args["state"] ? args["state"] : "";
    if (validStates.includes(state)) {
        let basinMeta = await getStateBasins(state);
        whenDataLoads(state, basinMeta);
    } else {
        loadInvalidState();
    }
    loadMainNwcc();
}

function loadInvalidState(states = validStates) {
    let msg = `
    <p>Only the 12 Western Basin States have Snow Survey Basins defined. Please append/replace one of the following states to the url 'state' argument.</p>
        <code>${states}</code>
        <p class="fsa-level">
            <a class="fsa-btn fsa-btn--small fsa-btn--secondary" href="https://www.nrcs.usda.gov/programs-initiatives/sswsf-snow-survey-and-water-supply-forecasting-program">More Info</a>
        </p>
    </p>
    `;
    renderGrowl("Not a valid Basin State!", msg, "error", true, 10);
}

export function parseUrl(basinName, basinType, plotType, element, state, parent) {
    const typeHash = { major: "_8", minor: "2_8", misc: "3" };
    if (basinName === "Misc. Basins") {
        return "noLink";
    }
    if (parent === "Misc. Basins") {
        basinType = "misc";
    }
    const urlSafeBasinName = encodeURIComponent(basinName.toLowerCase().replace(/ /g, "_").replace("'", ""));
    const searchQuery = new URLSearchParams(location.search);
    searchQuery.delete("state");
    const parseObj = {
        aws_por: `${plotBaseUrl}/POR/${element.toUpperCase()}/assocHUC${state.toLowerCase()}${typeHash[basinType]}/${urlSafeBasinName}.html${searchQuery.toString()}`,
        aws_proj: `${plotBaseUrl}/Proj/${element.split("-")[0].toUpperCase()}/assocHUC${state.toLowerCase()}${typeHash[basinType]}/${urlSafeBasinName}.html${searchQuery.toString()}`,
        swe_elev_mntly: `${plotBaseUrl}/swe-elev-plots/monthly/${currMonth}/${state.toLowerCase()}/${basinType}/${urlSafeBasinName}_swe_elev.html`,
        swe_elev_daily: `${plotBaseUrl}/swe-elev-plots/daily/${state.toLowerCase()}/${basinType}/${urlSafeBasinName}_swe_elev.html`,
        stacked: `${plotBaseUrl}/stacked-plots/${state.toLowerCase()}/${basinType}/${urlSafeBasinName}_${element.split("-")[0].toLowerCase()}_stacked.html`,
        reservoir: `${plotBaseUrl}/reservoir-plots/${currMonth}/${encodeURIComponent(state.toLowerCase())}_res.html`,
        westwide_reservoir: `${plotBaseUrl}/westwide-reservoir-plots/`,
    };
    const linkUrl = parseObj[plotType];
    if (searchQuery?.size) {
        return `${linkUrl}?${searchQuery.toString()}`;
    }
    return linkUrl;
}

function makeBasinElementSelect(element, state, majorBasinName, basinMeta, plotType = "aws_por") {
    let basinCnt = 0;
    Object.keys(basinMeta).forEach((majorBasinName) => {
        basinCnt += basinMeta[majorBasinName].length;
        if (majorBasinName.toLowerCase() != "misc. basins") {
            basinCnt += 1;
        }
    });
    const minorBasins = basinMeta[majorBasinName];
    const badge = document.getElementById(`${element.toLowerCase()}-badge`);
    badge.innerHTML = basinCnt.toString();
    const select = document.getElementById(`${element.toLowerCase()}-dd`);
    const optGrp = document.createElement("option");
    optGrp.classList.add("option-grp");
    optGrp.text = majorBasinName;
    optGrp.value = JSON.stringify([majorBasinName, "major", plotType, element.toUpperCase(), state, ""]);
    select.appendChild(optGrp);
    minorBasins.forEach(function (minorBasinName) {
        const option = document.createElement("option");
        option.value = JSON.stringify([
            minorBasinName,
            "minor",
            plotType,
            element.toUpperCase(),
            state,
            majorBasinName,
        ]);
        option.text = `  ${minorBasinName}`;
        option.classList.add("option-item");
        select.appendChild(option);
    });
}

function makeResElementSelect(state) {
    const resBadge = document.getElementById("res-badge");
    resBadge.innerHTML = `${validStates.length + 1}`; // +1 to account for westwide plot
    const resSelect = document.getElementById("res-dd");
    validStates.forEach(function (validState) {
        const option = document.createElement("option");
        option.value = JSON.stringify(["", "", "reservoir", "", validState.toUpperCase(), ""]);
        if (validState.toUpperCase() == state.toUpperCase()) {
            option.selected = "selected";
        }
        option.text = validStatesMap[validState];
        resSelect.appendChild(option);
    });
    //add westwide plot

    const option = document.createElement("option");
    option.value = JSON.stringify(["", "", "westwide_reservoir", "", "", ""]);
    option.text = "Westwide";
    resSelect.appendChild(option);
}

export function whenDataLoads(state, basinMeta) {
    makeResElementSelect(state);

    Object.keys(basinMeta).forEach(function (majorBasinName) {
        makeBasinElementSelect("wteq", state, majorBasinName, basinMeta, "aws_por");
        makeBasinElementSelect("prec", state, majorBasinName, basinMeta, "aws_por");
        makeBasinElementSelect("tavg", state, majorBasinName, basinMeta, "aws_por");

        makeBasinElementSelect("wteq-proj", state, majorBasinName, basinMeta, "aws_proj");
        makeBasinElementSelect("prec-proj", state, majorBasinName, basinMeta, "aws_proj");

        makeBasinElementSelect("wteq-elev", state, majorBasinName, basinMeta, "swe_elev_mntly");
        makeBasinElementSelect("wteq-elev-curr", state, majorBasinName, basinMeta, "swe_elev_daily");

        makeBasinElementSelect("wteq-stacked", state, majorBasinName, basinMeta, "stacked");
        makeBasinElementSelect("prec-stacked", state, majorBasinName, basinMeta, "stacked");
    });
}

export async function installOpenPlotListeners() {
    const openPlotButtons = document.querySelectorAll("button[id$=-btn]");
    openPlotButtons.forEach((button) => {
        button.addEventListener("click", function (e) {
            const selectId = this.id.replace("btn", "dd");
            const select = document.getElementById(selectId);
            openPlot(select.value);
        });
    });
}

function openPlot(parseUrlArgs) {
    const url = parseUrlArgs ? parseUrl(...JSON.parse(parseUrlArgs)) : false;
    if (url != "noLink" && url) {
        window.open(url, "_blank");
    }
}
